import React, { useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { base, CellDrop, useShowDrop } from '@sdxcorp/sdx-dds-gui-lib';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { SchedulerStatuses } from '../../../../admin/state/types';
import ProcessingDateLabel from './ProcessingDateLabel';
import { useNavigation } from '../../../../admin/routing';
import { colorValue } from '../../../../lib/theme';

const {
  atoms: {
    Icons: { Calendar },
  },
} = base;

export const schedulerRuntimeSchedulesQuery = gql`
  query SchedulerStatuses {
    schedulerStatuses {
      processingDate
    }
  }
`;

const StyledCalendar = styled(Calendar)`
  &&& {
    stroke: ${({ theme }) => colorValue(theme, c => c['grey-10'])};
    width: 15px;
    height: 15px;
  }

  margin-right: 6px;
  padding-bottom: 1px;
`;

interface ContainerProps {
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
`;

interface Props {
  i18TitleId?: string;
  shouldRefetchOnPathnameChange?: boolean;
  showCalendarIcon?: boolean;
}

const ProcessingDate: React.FC<Props> = ({ i18TitleId, shouldRefetchOnPathnameChange, showCalendarIcon }) => {
  const containerElt = useRef<HTMLDivElement>(null);
  const { pathname } = useNavigation();
  const { onMouseOver, onMouseOut, showDrop } = useShowDrop(true);
  const { t } = useTranslation();

  const { data, refetch } = useQuery<SchedulerStatuses>(schedulerRuntimeSchedulesQuery, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (shouldRefetchOnPathnameChange) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const date = data?.schedulerStatuses?.[0];

  if (showCalendarIcon) {
    return (
      <Container ref={containerElt} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
        <StyledCalendar fill="#90999A" />
        <ProcessingDateLabel i18TitleId={null} date={date?.processingDate} />
        <CellDrop showDrop={showDrop} targetRef={containerElt?.current}>
          {t('header.processingDate', { date: date?.processingDate })}
        </CellDrop>
      </Container>
    );
  }
  return <ProcessingDateLabel i18TitleId={i18TitleId} date={date?.processingDate} />;
};

export default ProcessingDate;
